/**
 * Leads-table column widths — the ONE source of truth.
 *
 * The table is `tableLayout: "fixed"`, and **Organisation is the only column
 * with no explicit width** — it absorbs whatever the fixed columns leave. That
 * made it the crumple zone: the fixed widths sum to ~1200px, and the sales
 * shell hands the table only ~1152px at a 1280px viewport, so Organisation was
 * handed `max(0, 1152 - 1202)` = **0px**. The org-name `<a>` — the only link in
 * the row — rendered at `0 x 20` px: in the DOM, invisible and unclickable.
 *
 * The fix is not to shave columns (measured 2026-08-31, there is no slack: the
 * only column with real headroom is Heat, and "Last signal" already overflows
 * its 96px). It is to stop the table from being squeezed below the width its
 * content needs. `leadsTableMinWidth()` sums the fixed widths of the columns
 * ACTUALLY rendered and adds `ORG_MIN_WIDTH`; the shell applies that as the
 * table's `minWidth`, so the `TableContainer`'s existing `overflowX: "auto"`
 * scrolls horizontally instead of collapsing the org name.
 *
 * Above that width the table still fills its container (`width: "100%"`), so
 * Organisation keeps absorbing all the extra space on a wide screen — the floor
 * only bites when the viewport is too narrow to seat every column.
 *
 * ⚠ **Adding a column?** Add its width HERE and include it in
 * `leadsTableMinWidth`. A width hardcoded at the call site would be missing
 * from the sum and would silently start eating Organisation's share again.
 * Pinned by `columnWidths.test.ts` + `cypress/e2e/sales_leads_table_columns.cy.ts`.
 */

export const LEADS_COL_WIDTH = {
  /** Wide enough for the longest urgency-elevated pill ("LAST CHANCE") on one line. */
  heat: 120,
  country: 84,
  /** "Found by" — the LeadSourceBadge (Lead Generator / Harvester). */
  source: 150,
  /** Fits the longest single-line vertical pill (22 uppercase chars) + the ▾ arrow. */
  vertical: 200,
  existingCustomer: 104,
  rfp: 128,
  timeline: 120,
  budget: 116,
  score: 96,
  confidence: 112,
  created: 96,
  last: 96,
  quality: 132,
  status: 112,
} as const;

/**
 * Floor for Organisation, the one auto-width column. 260px seats the bold org
 * name, the urgency badge under it, and the agent-summary line without the name
 * ellipsising after a couple of words (the longest sample names measured
 * ~275px). It is a FLOOR, not a width — Organisation still grows to take every
 * spare pixel on a wider screen.
 */
export const ORG_MIN_WIDTH = 260;

/** Which optional columns the table is currently rendering. Mirrors the flags
 * `LeadsTable` already computes, so the sum can never disagree with the header. */
export interface LeadsColumnFlags {
  /** `compact` drops the operator columns + Last signal. */
  compact: boolean;
  showStatus: boolean;
  /** Quality replaces Confidence rather than joining it. */
  showQuality: boolean;
  /** The data-gated placeholder columns (rendered only when some lead carries the value). */
  cols: { timeline: boolean; budget: boolean; score: boolean };
}

/**
 * The narrowest the table may be drawn: every fixed column at its full width
 * PLUS `ORG_MIN_WIDTH` for Organisation. Below this the container scrolls.
 */
export function leadsTableMinWidth({
  compact,
  showStatus,
  showQuality,
  cols,
}: LeadsColumnFlags): number {
  const w = LEADS_COL_WIDTH;
  let fixed = w.heat + w.country + w.source + w.vertical + w.created;

  if (!compact) {
    fixed += w.existingCustomer + w.rfp + w.last;
    if (cols.timeline) fixed += w.timeline;
    if (cols.budget) fixed += w.budget;
    if (cols.score) fixed += w.score;
  }
  if (!showQuality) fixed += w.confidence;
  if (showQuality) fixed += w.quality;
  if (showStatus) fixed += w.status;

  return fixed + ORG_MIN_WIDTH;
}
